import React, { useCallback, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
} from "react-native";
import dayjs from "dayjs";
import { useSelector, useDispatch } from "react-redux";
import { useFocusEffect, useIsFocused } from "@react-navigation/native";

import Statistics from "@assets/svg/Shop/Statistics";
import ChevronRight from "@assets/svg/ChevronRight";
import analytics from "@services/analytics";
import t from "@constants/localization";
import { MY_SHOP } from "@constants/analyticsEventsCT";
import testIds from "@constants/testIds";
import { getShopStats } from "@services/api";
import { colors } from "@constants/theme";
import { actions as shopActions } from "@reducers/shopReducer";

const PATH = "screens/Shop/components/Stats";

const Stats = ({ navigation }) => {
  const isFocused = useIsFocused();
  const dispatch = useDispatch();
  const { stats } = useSelector(({ shop }) => shop);
  const [isFetching, setIsFetching] = useState(true);

  const { user } = useSelector((state) => ({
    user: state.auth.user,
  }));

  const totalCustomers =
    stats?.notOrderedCustomers?.length + stats?.orderedCustomers?.length;

  useFocusEffect(
    useCallback(() => {
      if (isFocused) {
        fetchStats();
      }
    }, [isFocused]),
  );

  const fetchStats = async () => {
    try {
      const date = dayjs().format("YYYY-MM-DD");
      const { data } = await getShopStats({ id: user?.leader_id, date });
      dispatch(shopActions.setStats(data));
    } catch (error) {
      console.log(error);
    }
    setIsFetching(false);
  };

  const onCustomersPress = () => {
    analytics.trackEvent(MY_SHOP.TODAYS_CUSTOMER_CLICKED, {
      screen: "My Shop",
      cx_count: stats?.totalCustomers.length,
    });
    navigation.navigate("TodaysCustomers");
  };
  const onOrdersPress = () => {
    analytics.trackEvent(MY_SHOP.ORDERS_CLICKED, {
      screen: "My Shop",
      today_ord_count: stats?.noOfOrders,
    });
    navigation.navigate("OrdersList", {
      fromShop: true,
      screen_name: "today’s orders",
    });
  };
  return (
    <View style={styles.container}>
      <View style={styles.salesContainer}>
        <View style={{ flexDirection: "row" }}>
          <Statistics />
          <View style={styles.salesTextContainer}>
            <Text style={[styles.salesText, { paddingBottom: 2 }]}>
              {t(`${PATH}/todaysSales`)}
            </Text>
            <View>
              <View style={styles.salesCount}>
                <Text
                  style={[
                    styles.salesText,
                    { fontSize: 23, fontFamily: "NotoSansDevanagari-Regular" },
                  ]}
                >
                  ₹
                </Text>
                {isFetching ? (
                  <ActivityIndicator size="small" color="#FF7A00" />
                ) : (
                  <Text
                    style={[
                      styles.salesText,
                      {
                        fontSize: 24,
                        fontFamily: "NotoSansDevanagari-ExtraBold",
                      },
                    ]}
                  >
                    {stats?.totalSales.toFixed(0)}
                  </Text>
                )}
              </View>
            </View>
          </View>
        </View>
      </View>
      <View style={styles.customersOrdersContainer}>
        <TouchableOpacity
          testID={testIds.View_Customers}
          onPress={onCustomersPress}
          style={styles.customersOrdersTextContainer}
        >
          <View style={{ flexDirection: "row" }}>
            <Text style={styles.customersText}>
              {t(`${PATH}/customers`)} :{" "}
            </Text>
            {isFetching ? (
              <ActivityIndicator size="small" color={colors.lightBlack} />
            ) : (
              <Text style={styles.customerCount}>{totalCustomers ?? 0}</Text>
            )}
          </View>
          <View style={styles.chevronBtn}>
            <ChevronRight pathFill={colors.primary} width={12} height={12} />
          </View>
        </TouchableOpacity>
        <TouchableOpacity
          testID={testIds.View_Orders}
          onPress={onOrdersPress}
          style={styles.customersOrdersTextContainer}
        >
          <View style={{ flexDirection: "row" }}>
            <Text style={styles.ordersText}>{t(`${PATH}/orders`)} : </Text>
            {isFetching ? (
              <ActivityIndicator size="small" color={colors.lightBlack} />
            ) : (
              <Text style={styles.orderCount}>{stats?.noOfOrders || 0}</Text>
            )}
          </View>
          <View style={styles.chevronBtn}>
            <ChevronRight pathFill={colors.primary} width={12} height={12} />
          </View>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 16,
    backgroundColor: "#ffffff",
    elevation: 8,
  },
  salesContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 14,
    backgroundColor: "#ffffff",
    justifyContent: "space-between",
  },
  salesTextContainer: {
    marginLeft: 16,
  },
  salesText: {
    fontFamily: "NotoSansDevanagari-Regular",
    fontSize: 14,
    lineHeight: 28,
    color: "#FF7A00",
    justifyContent: "center",
  },
  salesCount: { flexDirection: "row", alignItems: "flex-start" },
  customersOrdersContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#ffffff",
  },
  customersText: {
    color: colors.lightBlack,
    fontSize: 14,
    fontFamily: "NotoSansDevanagari-Medium",
    alignSelf: "center",
  },
  customerCount: {
    fontFamily: "NotoSansDevanagari-ExtraBold",
    fontSize: 16,
    color: colors.black,
  },
  orderCount: {
    fontFamily: "NotoSansDevanagari-ExtraBold",
    fontSize: 16,
    color: colors.black,
  },
  ordersText: {
    color: colors.lightBlack,
    fontSize: 14,
    fontFamily: "NotoSansDevanagari-Medium",
    alignSelf: "center",
  },
  customersOrdersTextContainer: {
    flexDirection: "row",
    alignItems: "center",
    textAlign: "center",
    width: "47.5%",
    paddingHorizontal: 12,
    height: 40,
    marginHorizontal: 10,
    justifyContent: "space-between",
    borderRadius: 6,
    backgroundColor: colors.shadowLighter,
  },
  chevronBtn: {
    backgroundColor: "rgba(228, 0, 70, 0.1)",
    borderRadius: 10,
    padding: 4,
    alignItems: "center",
    justifyContent: "center",
  },
});

export default Stats;
